/**
 * `3a` — Perguntas do padrão de conformidade. Global, IOX-only (BR-23), sem
 * seletor de cliente.
 *
 * Três colunas, como o shot:
 *  - **esquerda** — árvore de grupos. Clicar num grupo de 1º nível abre o campo
 *    **peso do grupo**; subgrupo não tem peso;
 *  - **centro** — título + nota máxima CALCULADA (leitura, vem do servidor) e a
 *    tabela de perguntas do grupo selecionado;
 *  - **direita** — editor da pergunta: enunciado, grupo, opções ordenáveis com
 *    pontuação, orientação para a IA.
 *
 * O modal `3f` (novo padrão) abre por `?novo=1`, posto pelo botão do frame.
 */
import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router'

import type { Estrutura, Grupo, Pergunta } from '@/api/contracts/compliance'
import {
  useAtualizarGrupo,
  useCriarGrupo,
  useEstrutura,
  useExcluirPergunta,
  useSalvarPergunta,
} from '@/api/queries/compliance'
import { detalhesDoErro, mensagemDoErro } from '@/components/erros'
import { EsqueletoDeTabela, RegiaoDeCarregamento } from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { AreaDeTexto, Badge, Botao, Campo, Entrada } from '@/components/primitivos'
import { formatarDecimal } from '@/format'
import { usePermission } from '@/permissions/usePermission'

import { usePadraoSelecionado } from '../padrao'
import { lerNumero, paraCampo, rotuloDoGrupo } from '../rotulos'
import { NovoPadraoModal } from './NovoPadraoModal'

export function PerguntasDoPadrao() {
  const [params, setParams] = useSearchParams()
  const { consulta: padroes, padrao } = usePadraoSelecionado()
  const { editar } = usePermission('compliance.perguntas')
  const estrutura = useEstrutura(padrao?.id)
  const modalAberto = params.get('novo') === '1'

  function fecharModal(novoPadraoId?: string) {
    setParams((atuais) => {
      const proximos = new URLSearchParams(atuais)
      proximos.delete('novo')
      if (novoPadraoId) proximos.set('padrao', novoPadraoId)
      return proximos
    })
  }

  const modal = modalAberto && editar && (
    <NovoPadraoModal
      padroes={padroes.data ?? []}
      aoFechar={() => fecharModal()}
      aoCriar={(id) => fecharModal(id)}
    />
  )

  if (padroes.isError) {
    return <EstadoDeErro erro={padroes.error} aoTentarNovamente={() => void padroes.refetch()} />
  }
  if (padroes.isPending || (padrao && estrutura.isPending)) {
    return (
      <RegiaoDeCarregamento rotulo="Carregando perguntas do padrão…">
        <EsqueletoDeTabela linhas={6} colunas={4} />
      </RegiaoDeCarregamento>
    )
  }
  if (!padrao) {
    return (
      <>
        <EstadoVazio
          titulo="Nenhum padrão cadastrado."
          descricao={editar ? 'Crie o primeiro padrão pelo botão "novo padrão".' : undefined}
        />
        {modal}
      </>
    )
  }
  if (estrutura.isError) {
    return <EstadoDeErro erro={estrutura.error} aoTentarNovamente={() => void estrutura.refetch()} />
  }

  return (
    <>
      {/* 🔑 `key` no padrão: trocar de padrão zera seleção e formulários. */}
      <EditorDoPadrao key={padrao.id} estrutura={estrutura.data!} editar={editar} />
      {modal}
    </>
  )
}

function EditorDoPadrao({ estrutura, editar }: { estrutura: Estrutura; editar: boolean }) {
  const { grupos, perguntas, padrao } = estrutura
  const primeiroComPerguntas = grupos.find((g) => perguntas.some((p) => p.grupoId === g.id))
  const [idGrupo, setIdGrupo] = useState<string | undefined>(primeiroComPerguntas?.id ?? grupos[0]?.id)
  const [idPergunta, setIdPergunta] = useState<string | 'nova' | null>(null)

  const grupo = grupos.find((g) => g.id === idGrupo)
  const perguntaSelecionada = perguntas.find((p) => p.id === idPergunta)

  return (
    <section className="grid gap-5 xl:grid-cols-[18rem_minmax(0,1fr)_22rem]">
      <ArvoreDeGrupos
        padraoId={padrao.id}
        grupos={grupos}
        idSelecionado={idGrupo}
        aoSelecionar={(id) => {
          setIdGrupo(id)
          setIdPergunta(null)
        }}
        editar={editar}
      />

      <div className="flex min-w-0 flex-col gap-4">
        {grupo ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <h2 className="type-h4 text-text-primary">
                  {grupo.paiId ? `${grupo.codigo} ${grupo.titulo}` : `Pergunta ${grupo.codigo}`}
                </h2>
                <Badge>{grupo.qtdItens} itens</Badge>
              </div>
              {editar && (
                <Botao icone="plus" onClick={() => setIdPergunta('nova')}>
                  adicionar pergunta
                </Botao>
              )}
            </div>

            <FaixaDoGrupo key={grupo.id} grupo={grupo} editar={editar} />

            <TabelaDePerguntas
              perguntas={perguntas.filter((p) => p.grupoId === grupo.id)}
              idSelecionada={idPergunta}
              aoSelecionar={setIdPergunta}
              editar={editar}
            />
          </>
        ) : (
          <EstadoVazio
            titulo="Este padrão ainda não tem grupos."
            descricao={editar ? 'Comece por "adicionar grupo", na coluna da esquerda.' : undefined}
          />
        )}

        <p className="rounded-card border-border-subtle bg-subtle text-text-body flex gap-3 border p-4 type-sm">
          <i className="ph ph-info text-iox-navy mt-0.5" aria-hidden="true" />
          <span>
            As opções de resposta variam por pergunta e por ano — por isso a lista de opções e a
            pontuação de cada opção são cadastradas item a item. O ícone{' '}
            <i className="ph ph-sparkle text-iox-teal" aria-hidden="true" /> indica perguntas que já
            têm orientação para a IA.
          </span>
        </p>
      </div>

      <div className="border-border-subtle bg-surface-card rounded-card self-start border p-5">
        {idPergunta === null ? (
          <p className="text-text-muted type-sm">
            {editar
              ? 'Selecione uma pergunta na tabela para editar, ou use "adicionar pergunta".'
              : 'Selecione uma pergunta na tabela para ver o cadastro.'}
          </p>
        ) : (
          <EditorDePergunta
            key={idPergunta}
            padraoId={padrao.id}
            pergunta={perguntaSelecionada}
            grupos={grupos}
            grupoInicial={grupo?.id}
            editar={editar}
            aoFechar={() => setIdPergunta(null)}
          />
        )}
      </div>
    </section>
  )
}

// ── Esquerda: árvore ────────────────────────────────────────────────────────
function ArvoreDeGrupos({
  padraoId,
  grupos,
  idSelecionado,
  aoSelecionar,
  editar,
}: {
  padraoId: string
  grupos: Grupo[]
  idSelecionado: string | undefined
  aoSelecionar: (id: string) => void
  editar: boolean
}) {
  const [criando, setCriando] = useState<'grupo' | 'subgrupo' | null>(null)
  const raizes = grupos.filter((g) => g.paiId === null)
  const selecionado = grupos.find((g) => g.id === idSelecionado)
  const raizDaSelecao = selecionado?.paiId ?? selecionado?.id

  return (
    <nav aria-label="Estrutura do padrão" className="flex flex-col gap-3">
      <span className="type-eyebrow text-text-muted">Estrutura do padrão</span>
      {raizes.length === 0 && <p className="text-text-muted type-sm">Nenhum grupo cadastrado.</p>}
      <ul className="flex flex-col gap-2">
        {raizes.map((raiz) => {
          const ativo = raiz.id === idSelecionado
          return (
            <li key={raiz.id} className="flex flex-col gap-1">
              <div
                className={`rounded-card border p-3 ${ativo ? 'border-navy-200 bg-navy-50' : 'border-transparent'}`}
              >
                <button
                  type="button"
                  aria-current={ativo}
                  onClick={() => aoSelecionar(raiz.id)}
                  className="flex w-full flex-col items-start text-left"
                >
                  <span className="type-label text-text-primary">Pergunta {raiz.codigo}</span>
                  <span className="type-sm text-text-body">{raiz.titulo}</span>
                  <span className="type-sm text-iox-navy font-semibold">
                    {raiz.qtdItens} itens · peso {formatarDecimal(raiz.peso ?? 0)} · máx.{' '}
                    {formatarDecimal(raiz.notaMaxima)}
                  </span>
                </button>
                {ativo && <PesoDoGrupo key={raiz.id} grupo={raiz} editar={editar} />}
              </div>
              <ul className="border-border-subtle ml-4 flex flex-col border-l">
                {grupos
                  .filter((g) => g.paiId === raiz.id)
                  .map((sub) => {
                    const subAtivo = sub.id === idSelecionado
                    return (
                      <li key={sub.id}>
                        <button
                          type="button"
                          aria-current={subAtivo}
                          onClick={() => aoSelecionar(sub.id)}
                          className={`flex w-full flex-col items-start px-3 py-2 text-left ${
                            subAtivo ? 'border-iox-navy bg-subtle -ml-px border-l-2' : ''
                          }`}
                        >
                          <span className={`type-sm ${subAtivo ? 'text-iox-navy font-semibold' : 'text-text-body'}`}>
                            {sub.codigo} {sub.titulo}
                          </span>
                          <span className="type-sm text-text-muted">{sub.qtdItens} itens</span>
                        </button>
                      </li>
                    )
                  })}
              </ul>
            </li>
          )
        })}
      </ul>

      <p className="border-border-subtle text-text-muted border-t pt-3 type-sm">
        Configuração global: as mesmas perguntas valem para todos os clientes.{' '}
        <strong>O peso pertence ao grupo de primeiro nível</strong> e é editado aqui na árvore,
        clicando no grupo. Subgrupos não têm peso próprio.
      </p>

      {editar && (
        <div className="flex flex-wrap gap-2">
          <Botao icone="plus" onClick={() => setCriando('grupo')}>
            adicionar grupo
          </Botao>
          <Botao
            variante="discreta"
            icone="plus"
            disabled={!raizDaSelecao}
            title={raizDaSelecao ? undefined : 'selecione um grupo de primeiro nível'}
            onClick={() => setCriando('subgrupo')}
          >
            adicionar subgrupo
          </Botao>
        </div>
      )}
      {editar && criando && (
        <NovoGrupo
          key={criando}
          padraoId={padraoId}
          pai={criando === 'subgrupo' ? grupos.find((g) => g.id === raizDaSelecao) : undefined}
          aoFechar={() => setCriando(null)}
          aoCriar={aoSelecionar}
        />
      )}
    </nav>
  )
}

function PesoDoGrupo({ grupo, editar }: { grupo: Grupo; editar: boolean }) {
  const atualizar = useAtualizarGrupo()
  const [peso, setPeso] = useState(paraCampo(grupo.peso ?? 0))
  const valor = lerNumero(peso)

  return (
    <form
      className="border-border-subtle mt-3 flex flex-col gap-1.5 border-t pt-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (valor !== null) atualizar.mutate({ id: grupo.id, peso: valor })
      }}
    >
      <label className="flex flex-col gap-1.5">
        <span className="type-eyebrow text-text-muted">
          Peso do grupo <span className="text-danger-700">*</span>
        </span>
        <span className="flex gap-2">
          <Entrada
            aria-label={`Peso do grupo ${grupo.codigo}`}
            inputMode="decimal"
            value={peso}
            disabled={!editar}
            onChange={(e) => setPeso(e.target.value)}
          />
          {editar && (
            <Botao variante="primaria" type="submit" disabled={valor === null || atualizar.isPending}>
              {atualizar.isPending ? 'salvando…' : 'salvar'}
            </Botao>
          )}
        </span>
      </label>
      {valor === null && <span className="type-sm text-danger-700">Informe o peso (ex.: 0,2).</span>}
      {atualizar.isError && <span className="type-sm text-danger-700">{mensagemDoErro(atualizar.error)}</span>}
    </form>
  )
}

function NovoGrupo({
  padraoId,
  pai,
  aoFechar,
  aoCriar,
}: {
  padraoId: string
  pai: Grupo | undefined
  aoFechar: () => void
  aoCriar: (id: string) => void
}) {
  const criar = useCriarGrupo(padraoId)
  const [codigo, setCodigo] = useState(pai ? `${pai.codigo}.` : '')
  const [titulo, setTitulo] = useState('')
  const [peso, setPeso] = useState('')
  const pesoLido = lerNumero(peso)
  const valido = codigo.trim() !== '' && titulo.trim() !== '' && (pai !== undefined || pesoLido !== null)

  return (
    <form
      aria-label={pai ? 'Novo subgrupo' : 'Novo grupo'}
      className="border-border-subtle bg-surface-card rounded-card flex flex-col gap-3 border p-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (!valido) return
        criar.mutate(
          {
            paiId: pai?.id ?? null,
            codigo: codigo.trim(),
            titulo: titulo.trim(),
            ...(pai ? {} : { peso: pesoLido! }),
          },
          {
            onSuccess: (novo) => {
              aoCriar(novo.id)
              aoFechar()
            },
          },
        )
      }}
    >
      <span className="type-label text-text-primary">
        {pai ? `Novo subgrupo de Pergunta ${pai.codigo}` : 'Novo grupo de primeiro nível'}
      </span>
      <Campo rotulo="Código" obrigatorio>
        <Entrada value={codigo} onChange={(e) => setCodigo(e.target.value)} />
      </Campo>
      <Campo rotulo="Título" obrigatorio>
        <Entrada value={titulo} onChange={(e) => setTitulo(e.target.value)} />
      </Campo>
      {!pai && (
        <Campo rotulo="Peso do grupo" obrigatorio hint="ex.: 0,2">
          <Entrada inputMode="decimal" value={peso} onChange={(e) => setPeso(e.target.value)} />
        </Campo>
      )}
      {criar.isError && <p className="type-sm text-danger-700">{mensagemDoErro(criar.error)}</p>}
      <div className="flex gap-2">
        <Botao variante="primaria" type="submit" disabled={!valido || criar.isPending}>
          {pai ? 'criar subgrupo' : 'criar grupo'}
        </Botao>
        <Botao onClick={aoFechar}>cancelar</Botao>
      </div>
    </form>
  )
}

// ── Centro ──────────────────────────────────────────────────────────────────
function FaixaDoGrupo({ grupo, editar }: { grupo: Grupo; editar: boolean }) {
  const atualizar = useAtualizarGrupo()
  const [titulo, setTitulo] = useState(grupo.titulo)
  const sub = grupo.paiId !== null

  return (
    <form
      className="border-border-subtle bg-subtle rounded-card flex flex-wrap items-end gap-4 border p-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault()
        if (titulo.trim()) atualizar.mutate({ id: grupo.id, titulo: titulo.trim() })
      }}
    >
      <label className="flex min-w-60 flex-1 flex-col gap-1.5">
        <span className="type-eyebrow text-text-muted">{sub ? 'Título do subgrupo' : 'Título do grupo'}</span>
        <Entrada value={titulo} disabled={!editar} onChange={(e) => setTitulo(e.target.value)} />
      </label>
      <div className="flex flex-col gap-1.5">
        <span className="type-eyebrow text-text-muted" id={`nota-max-${grupo.id}`}>
          Nota máxima
        </span>
        <output
          aria-labelledby={`nota-max-${grupo.id}`}
          className="rounded-field border-border-strong text-text-primary border border-dashed px-3 py-2 type-body"
        >
          <strong>{formatarDecimal(grupo.notaMaxima)}</strong>{' '}
          <span className="text-text-muted type-sm">calculada</span>
        </output>
      </div>
      {editar && (
        <Botao type="submit" disabled={!titulo.trim() || atualizar.isPending}>
          {sub ? 'salvar subgrupo' : 'salvar grupo'}
        </Botao>
      )}
      {sub && (
        <p className="text-text-muted w-full type-sm">
          Subgrupo não tem peso: a pontuação dos itens é somada dentro do grupo pai e multiplicada pelo
          peso dele. A nota máxima é calculada a partir das pontuações cadastradas, nunca digitada.
        </p>
      )}
      {atualizar.isError && <p className="type-sm text-danger-700 w-full">{mensagemDoErro(atualizar.error)}</p>}
    </form>
  )
}

function TabelaDePerguntas({
  perguntas,
  idSelecionada,
  aoSelecionar,
  editar,
}: {
  perguntas: Pergunta[]
  idSelecionada: string | null
  aoSelecionar: (id: string) => void
  editar: boolean
}) {
  if (perguntas.length === 0) {
    return (
      <div className="border-border-subtle bg-surface-card rounded-card border">
        <EstadoVazio titulo="Nenhuma pergunta neste grupo." />
      </div>
    )
  }

  return (
    <div className="border-border-subtle bg-surface-card rounded-card overflow-x-auto border">
      <table className="w-full text-left">
        <thead className="bg-subtle">
          <tr className="type-eyebrow text-text-muted">
            <th className="px-4 py-3">Item</th>
            <th className="px-4 py-3">Enunciado</th>
            <th className="px-4 py-3">Opções e pontuação</th>
            <th className="px-4 py-3">IA</th>
            <th className="px-4 py-3">
              <span className="sr-only">Ações</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {perguntas.map((p) => {
            const ativa = p.id === idSelecionada
            return (
              <tr
                key={p.id}
                aria-selected={ativa}
                className={`border-border-subtle border-t ${ativa ? 'bg-navy-50 shadow-[inset_4px_0_0_var(--iox-navy)]' : ''}`}
              >
                <td className="text-text-primary px-4 py-3 type-label whitespace-nowrap">{p.codigo}</td>
                <td className="text-text-body px-4 py-3 type-body">{p.enunciado}</td>
                <td className="px-4 py-3">
                  <span className="flex flex-wrap gap-1.5">
                    {p.opcoes.map((o) => (
                      <span
                        key={o.id}
                        className={`rounded-control px-2.5 py-1 type-sm whitespace-nowrap ${
                          o.pontuacao > 0 ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-text-body'
                        }`}
                      >
                        {o.rotulo} · {formatarDecimal(o.pontuacao)}
                      </span>
                    ))}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <i
                    className={`ph ph-sparkle text-lg ${p.orientacaoIA ? 'text-iox-teal' : 'text-icon-muted'}`}
                    role="img"
                    aria-label={p.orientacaoIA ? 'com orientação para a IA' : 'sem orientação para a IA'}
                  />
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    aria-label={`${editar ? 'editar' : 'ver'} pergunta ${p.codigo}`}
                    onClick={() => aoSelecionar(p.id)}
                    className="text-icon-default hover:text-iox-teal duration-fast"
                  >
                    <i className={`ph ${editar ? 'ph-pencil-simple' : 'ph-eye'}`} aria-hidden="true" />
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Direita: editor da pergunta ─────────────────────────────────────────────
interface OpcaoEditavel {
  chave: number
  rotulo: string
  pontuacao: string
}

let proximaChave = 0

function EditorDePergunta({
  padraoId,
  pergunta,
  grupos,
  grupoInicial,
  editar,
  aoFechar,
}: {
  padraoId: string
  pergunta: Pergunta | undefined
  grupos: Grupo[]
  grupoInicial: string | undefined
  editar: boolean
  aoFechar: () => void
}) {
  const salvar = useSalvarPergunta(padraoId)
  const excluir = useExcluirPergunta()
  const [codigo, setCodigo] = useState(pergunta?.codigo ?? '')
  const [enunciado, setEnunciado] = useState(pergunta?.enunciado ?? '')
  const [grupoId, setGrupoId] = useState(pergunta?.grupoId ?? grupoInicial ?? '')
  const [orientacao, setOrientacao] = useState(pergunta?.orientacaoIA ?? '')
  const [opcoes, setOpcoes] = useState<OpcaoEditavel[]>(() =>
    (pergunta?.opcoes ?? []).map((o) => ({
      chave: proximaChave++,
      rotulo: o.rotulo,
      pontuacao: paraCampo(o.pontuacao),
    })),
  )
  const [tentou, setTentou] = useState(false)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false)

  const faltando = [
    ...(codigo.trim() ? [] : ['Código']),
    ...(enunciado.trim() ? [] : ['Enunciado']),
    ...(grupoId ? [] : ['Grupo']),
    ...(opcoes.length > 0 && opcoes.every((o) => o.rotulo.trim() && lerNumero(o.pontuacao) !== null)
      ? []
      : ['Opções de resposta e pontuação']),
  ]
  const camposDoServidor = salvar.isError
    ? ((detalhesDoErro(salvar.error).camposFaltantes as string[] | undefined) ?? [])
    : []

  function mover(indice: number, delta: -1 | 1) {
    setOpcoes((atuais) => {
      const alvo = indice + delta
      if (alvo < 0 || alvo >= atuais.length) return atuais
      const copia = [...atuais]
      ;[copia[indice], copia[alvo]] = [copia[alvo]!, copia[indice]!]
      return copia
    })
  }

  function aoSalvar(e: FormEvent) {
    e.preventDefault()
    setTentou(true)
    if (faltando.length > 0) return
    salvar.mutate(
      {
        id: pergunta?.id ?? null,
        grupoId,
        codigo: codigo.trim(),
        enunciado: enunciado.trim(),
        orientacaoIA: orientacao.trim() || null,
        opcoes: opcoes.map((o) => ({ rotulo: o.rotulo.trim(), pontuacao: lerNumero(o.pontuacao)! })),
      },
      { onSuccess: aoFechar },
    )
  }

  const titulo = pergunta ? `${editar ? 'Editar' : 'Pergunta'} ${pergunta.codigo}` : 'Nova pergunta'

  return (
    <form aria-label={titulo} className="flex flex-col gap-4" onSubmit={aoSalvar}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="type-h4 text-text-primary">{pergunta && editar ? `Editar pergunta ${pergunta.codigo}` : titulo}</h3>
        {pergunta && editar && (
          <button
            type="button"
            aria-label={`excluir pergunta ${pergunta.codigo}`}
            onClick={() => setConfirmandoExclusao(true)}
            className="text-icon-default hover:text-danger-700 duration-fast"
          >
            <i className="ph ph-trash" aria-hidden="true" />
          </button>
        )}
      </div>

      {confirmandoExclusao && pergunta && (
        <div role="alertdialog" aria-label="Confirmar exclusão" className="rounded-card bg-danger-100 flex flex-col gap-2 p-3">
          <p className="type-sm text-danger-700">
            Excluir a pergunta {pergunta.codigo}? As respostas dos clientes a ela deixam de contar.
          </p>
          <div className="flex gap-2">
            <Botao
              variante="primaria"
              disabled={excluir.isPending}
              onClick={() => excluir.mutate(pergunta.id, { onSuccess: aoFechar })}
            >
              excluir pergunta
            </Botao>
            <Botao onClick={() => setConfirmandoExclusao(false)}>cancelar</Botao>
          </div>
          {excluir.isError && <p className="type-sm text-danger-700">{mensagemDoErro(excluir.error)}</p>}
        </div>
      )}

      {/* ⚠️ assumido — §9: o shot não desenha "Código", mas o contrato exige `codigo`
          ao criar a pergunta. Sem o campo, "adicionar pergunta" não teria como numerar o item. */}
      <Campo rotulo="Código" obrigatorio hint="numeração hierárquica, ex.: 20.2.17">
        <Entrada value={codigo} disabled={!editar} onChange={(e) => setCodigo(e.target.value)} />
      </Campo>
      <Campo rotulo="Enunciado" obrigatorio>
        <AreaDeTexto rows={3} value={enunciado} disabled={!editar} onChange={(e) => setEnunciado(e.target.value)} />
      </Campo>
      <Campo rotulo="Grupo" obrigatorio>
        <select
          value={grupoId}
          disabled={!editar}
          onChange={(e) => setGrupoId(e.target.value)}
          className="rounded-field border-border-subtle bg-surface-card text-text-primary type-body w-full border px-3 py-2 disabled:bg-gray-50"
        >
          <option value="">selecione…</option>
          {grupos.map((g) => (
            <option key={g.id} value={g.id}>
              {rotuloDoGrupo(g, grupos)}
            </option>
          ))}
        </select>
      </Campo>

      <fieldset className="flex flex-col gap-2">
        <legend className="type-eyebrow text-text-muted mb-1">
          Opções de resposta e pontuação <span className="text-danger-700">*</span>
        </legend>
        {opcoes.length === 0 && <p className="text-text-muted type-sm">Nenhuma opção cadastrada.</p>}
        <ol className="flex flex-col gap-2">
          {opcoes.map((opcao, indice) => (
            <li key={opcao.chave} className="border-border-subtle rounded-field flex items-center gap-2 border px-2 py-1.5">
              {editar && (
                <span className="flex flex-col">
                  <button
                    type="button"
                    aria-label={`mover opção ${indice + 1} para cima`}
                    disabled={indice === 0}
                    onClick={() => mover(indice, -1)}
                    className="text-icon-default disabled:opacity-30"
                  >
                    <i className="ph ph-caret-up" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={`mover opção ${indice + 1} para baixo`}
                    disabled={indice === opcoes.length - 1}
                    onClick={() => mover(indice, 1)}
                    className="text-icon-default disabled:opacity-30"
                  >
                    <i className="ph ph-caret-down" aria-hidden="true" />
                  </button>
                </span>
              )}
              <Entrada
                aria-label={`Rótulo da opção ${indice + 1}`}
                value={opcao.rotulo}
                disabled={!editar}
                onChange={(e) =>
                  setOpcoes((atuais) => atuais.map((o) => (o.chave === opcao.chave ? { ...o, rotulo: e.target.value } : o)))
                }
              />
              <Entrada
                aria-label={`Pontuação da opção ${indice + 1}`}
                inputMode="decimal"
                className="w-20"
                value={opcao.pontuacao}
                disabled={!editar}
                onChange={(e) =>
                  setOpcoes((atuais) =>
                    atuais.map((o) => (o.chave === opcao.chave ? { ...o, pontuacao: e.target.value } : o)),
                  )
                }
              />
              {editar && (
                <button
                  type="button"
                  aria-label={`remover opção ${indice + 1}`}
                  onClick={() => setOpcoes((atuais) => atuais.filter((o) => o.chave !== opcao.chave))}
                  className="text-icon-default hover:text-danger-700"
                >
                  <i className="ph ph-x" aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ol>
        {editar && (
          <Botao
            variante="discreta"
            icone="plus-circle"
            className="self-start"
            onClick={() => setOpcoes((atuais) => [...atuais, { chave: proximaChave++, rotulo: '', pontuacao: '' }])}
          >
            adicionar opção
          </Botao>
        )}
      </fieldset>

      <Campo rotulo="Orientação para a IA" hint="opcional — insumo para o julgamento da evidência">
        <AreaDeTexto rows={5} value={orientacao} disabled={!editar} onChange={(e) => setOrientacao(e.target.value)} />
      </Campo>

      {tentou && faltando.length > 0 && (
        <p role="alert" className="type-sm text-danger-700">
          Preencha: {faltando.join(', ')}.
        </p>
      )}
      {salvar.isError && (
        <p role="alert" className="type-sm text-danger-700">
          {mensagemDoErro(salvar.error)}
          {camposDoServidor.length > 0 && ` Campos: ${camposDoServidor.join(', ')}.`}
        </p>
      )}

      {editar ? (
        <div className="flex gap-2">
          <Botao variante="primaria" type="submit" disabled={salvar.isPending}>
            {salvar.isPending ? 'salvando…' : 'salvar'}
          </Botao>
          <Botao onClick={aoFechar}>cancelar</Botao>
        </div>
      ) : (
        <Botao onClick={aoFechar} className="self-start">
          fechar
        </Botao>
      )}
    </form>
  )
}
